import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api/client';
import type {
  Sponsorship,
  SponsorshipInput,
  SponsoredBusiness,
  SponsoredItem,
} from './types';

/**
 * Sponsorships.
 *
 * A sponsorship is a plan, not a live thing. The record holds a name and a
 * window; two join tables hold the items and the stores. Nothing reaches the
 * catalogue until **Apply**, which walks every item × store pair and stamps the
 * window onto that `BusinessItem`:
 *
 *   businessItem.SponsorshipStartDate = sponsorship.StartDate
 *   businessItem.SponsorshipEndDate   = sponsorship.EndDate
 *
 * **Cancel** walks the same pairs and sets both dates to ten days ago, which is
 * how the API expresses "expired". Neither writes anything back to the
 * sponsorship, so the record cannot tell you whether it has been applied — the
 * page says so rather than implying a state it cannot know.
 */

const KEY = ['sponsorships'] as const;

interface Paged<T> {
  results: T[];
}

export function useSponsorships() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<Paged<Sponsorship>>('marketing/sponsorships'),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) => Date.parse(b.startDate) - Date.parse(a.startDate)
      ),
    staleTime: 60_000,
  });
}

export function useSponsorship(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'one', id],
    queryFn: () => get<Sponsorship>(`marketing/sponsorship/${id}`),
    enabled: !!id,
  });
}

export function useSponsorshipItems(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'items', id],
    queryFn: () => get<Paged<SponsoredItem>>(`marketing/sponsorship/items/${id}`),
    select: (data) => data.results ?? [],
    enabled: !!id,
  });
}

export function useSponsorshipBusinesses(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'businesses', id],
    queryFn: () =>
      get<Paged<SponsoredBusiness>>(`marketing/sponsorship/businesses/${id}`),
    select: (data) => data.results ?? [],
    enabled: !!id,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/** Create and update are the same POST, decided by `id`. */
export function useSaveSponsorship() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (values: SponsorshipInput) =>
      post<Sponsorship>('marketing/sponsorships', values),
    onSuccess: invalidate,
  });
}

export function useDeleteSponsorship() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) => del<unknown>(`marketing/sponsorship/${id}`),
    onSuccess: invalidate,
  });
}

export function useAddItem(sponsorshipId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (itemId: string) =>
      post<unknown>(
        `marketing/sponsorship/items/add/${sponsorshipId}/${itemId}`,
        {}
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveItem(sponsorshipId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (itemId: string) =>
      post<unknown>(
        `marketing/sponsorship/items/remove/${sponsorshipId}/${itemId}`,
        {}
      ),
    onSuccess: invalidate,
  });
}

export function useAddBusiness(sponsorshipId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (businessId: string) =>
      post<unknown>(
        `marketing/sponsorship/businesses/add/${sponsorshipId}/${businessId}`,
        {}
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveBusiness(sponsorshipId: string) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (businessId: string) =>
      post<unknown>(
        `marketing/sponsorship/businesses/remove/${sponsorshipId}/${businessId}`,
        {}
      ),
    onSuccess: invalidate,
  });
}

/** Stamp the window onto every item × store pair. */
export function useApplySponsorship() {
  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`marketing/sponsorships/apply/${id}`, {}),
  });
}

/**
 * Expire every pair.
 *
 * Note the plural: the route is `sponsorships/cancel/{id}`. The original called
 * `sponsorship/cancel/{id}` and there is no such route, so its Cancel button
 * has never done anything but 404.
 */
export function useCancelSponsorship() {
  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`marketing/sponsorships/cancel/${id}`, {}),
  });
}
